import {
  DestroyRef,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { UnleashService } from "@karelics/angular-unleash-proxy-client";
import { combineLatest, distinctUntilChanged, filter, map, startWith } from "rxjs";

import { OrgFacade } from "state/org.facade";
import { UserFacade } from "state/user.facade";

export const provideUnleashContextSyncInitializer = (
  queryParamKey: string = "orgId"
): EnvironmentProviders => {
  return makeEnvironmentProviders([
    provideAppInitializer((): void => {
      try {
        const router = inject(Router);
        const unleashService = inject(UnleashService);
        const destroyRef = inject(DestroyRef);
        const userFacade = inject(UserFacade);
        const orgFacade = inject(OrgFacade);

        const routeOrgId$ = router.events.pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          map(() => {
            let route = router.routerState.root.snapshot;
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route.queryParamMap.get(queryParamKey);
          }),
          startWith(null)
        );

        combineLatest([userFacade.userId$, orgFacade.fullOrSharedOrgId$, routeOrgId$])
          .pipe(
            takeUntilDestroyed(destroyRef),
            map(([userId, storeOrgId, routeOrgId]) => {
              // Route orgId overrides store orgId
              const effectiveOrgId = routeOrgId ?? storeOrgId;

              return {
                userId,
                orgId: effectiveOrgId,
              };
            }),
            distinctUntilChanged(
              (prev, curr) => prev.userId === curr.userId && prev.orgId === curr.orgId
            )
          )
          .subscribe(({ userId, orgId }) => {
            // Fall back to "null" string as Unleash client SDK filters out null/undefined values
            unleashService?.unleash.updateContext({
              userId: userId ?? "null",
              properties: {
                orgId: orgId ?? "null",
              },
            });

            void unleashService?.unleash.updateToggles();
          });
      } catch {
        console.error("Failed to sync Unleash context");
      }
    }),
  ]);
};
